import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { Header } from '@components/Header';
import AddIcon from '@components/icons/Add.svg';
import { PagePagination } from '@components/core-ui/Pagination';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { toUnixSecondsQueryParam } from '@helpers/toUnixSecondsQueryParam';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { usePermission } from '@providers/PermissionProvider/usePermission';
import { useAPI } from '@providers/useAPI';
import { routes } from '@routes';
import { useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useJobsFilterQueryParams } from 'app/hooks/useJobsFilterQueryParams';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { createFetchOrganizationRepositoriesQueryFn } from 'app/queries/repositories/createFetchOrganizationRepositoriesQueryFn';
import { repositoriesKeys, TRepositoriesListParams } from 'app/queries/repositories/repositoriesKeys';
import { EPermission } from 'app/types/enums/EPermission';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { TCreatedFilter } from './Filter/types/TCreatedFilter';
import { Content } from './styles/Content';
import { StyledLink } from './styles/StyledLink';
import { Wrapper } from './styles/Wrapper';

const SEARCH_DEBOUNCE_MS = 300;

export const RepositoriesListPage = () => {
  const { call } = useAPI();
  const { openModal } = useModalContext();
  const navigate = useNavigate();
  const { searchParams, page, pageSize, search, setSearch, setPage, setPageSize, updateParams } = useListQueryParams({
    defaults: { pageSize: 25, orderBy: 'name', orderType: 'asc' },
  });
  const { jobsFilter: createdFilter, applyJobsFilter: applyCreatedFilter } = useJobsFilterQueryParams({
    searchParams,
    updateParams,
  });

  const { organization } = useAuthContext();
  const organizationId = organization?.id ?? '';

  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;
  const from = toUnixSecondsQueryParam(createdFilter.from);
  const to = toUnixSecondsQueryParam(createdFilter.to);
  const listParams = useMemo<TRepositoriesListParams>(
    () => ({ page, pageSize, search: debouncedSearch, from, to }),
    [debouncedSearch, from, page, pageSize, to],
  );

  const repositoriesQuery = useQuery({
    queryKey: repositoriesKeys.list(organizationId, listParams),
    queryFn: createFetchOrganizationRepositoriesQueryFn(call),
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[0].organizationId === organizationId ? previousData : undefined,
    enabled: organizationId !== '' && !isSearchSettling,
  });
  const repositories = repositoriesQuery.data?.data ?? [];
  const repositoriesCount = repositoriesQuery.data?.count ?? 0;

  const handleAddCase = () => {
    navigate(
      {
        pathname: routes.newRepository.make(organization?.slug ?? ''),
      },
      {
        state: {
          from: routes.repositoriesList.make(organization?.slug ?? ''),
        },
      },
    );
  };

  const [, canEditRepositories] = usePermission(EPermission.editRepositories);

  const openFiltersModal = () => {
    openModal(ModalNamesEnum.CasesFilters, {
      createdFilter,
      createdFilterOnChange: () => undefined,
      onSubmit: (filter: TCreatedFilter) =>
        applyCreatedFilter(filter, { onUnchanged: () => void repositoriesQuery.refetch() }),
    });
  };

  return (
    <>
      <Helmet>
        <title>Cases List - AutoScript</title>
      </Helmet>
      <Header
        title="Cases"
        setSearch={setSearch}
        search={search}
        primaryAction={
          canEditRepositories
            ? {
                key: 'create-case',
                label: 'Create New Case',
                icon: AddIcon,
                onClick: handleAddCase,
                testId: 'header_case_button',
              }
            : undefined
        }
        onFilterClick={openFiltersModal}
        filterCounter={createdFilter.from || createdFilter.to ? 1 : 0}
      />
      <Content>
        <LinearProgressLoader active={repositoriesQuery.isFetching || isSearchSettling} />
        <Wrapper>
          {repositoriesQuery.isError ? (
            <div className="mb-4 flex items-center gap-3 text-xs text-[var(--color-text-muted)]" role="alert">
              <span>Failed to load cases.</span>
              <TextButton type="button" onClick={() => void repositoriesQuery.refetch()}>
                Retry
              </TextButton>
            </div>
          ) : null}
          <TableWrapper>
            <Table>
              <Thead>
                <Tr>
                  <Th>Case Title</Th>
                  <Th>Created</Th>
                  <Th>Jobs (Processing / Ready For Editing / Complete)</Th>
                </Tr>
              </Thead>
              <Tbody>
                {repositories.map((data) => (
                  <Tr key={data.id}>
                    <Td>
                      <p>
                        <StyledLink
                          to={{
                            pathname: routes.repositoryDetails.make(organization?.slug ?? '', data.slug, data.id),
                          }}
                          state={{
                            from: routes.repositoriesList.make(organization?.slug ?? ''),
                          }}
                        >
                          {data.name}
                        </StyledLink>
                      </p>
                    </Td>
                    <Td>
                      <p>{getAmericanTime(data.createdAt)}</p>
                    </Td>
                    <Td>
                      <p>
                        {data.totalJobs} ({data.jobsProcessing} / {data.jobsReadyForTranscribing} / {data.jobsComplete})
                      </p>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableWrapper>
          <PagePagination
            totalItemsCount={repositoriesCount}
            page={page}
            onPageChange={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </Wrapper>
      </Content>
    </>
  );
};
